import { Check } from 'lucide-react'

import type { Post } from '@/api/types'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/cn'
import { useFormat } from '@/lib/format'

import { useVote } from '../hooks/usePostMutations'

/**
 * 帖子里的投票（D78）。
 *
 * 票数看得到（我投过、我是作者、或者已截止）就画条形图；看不到就给一排按钮。
 * 投票前不给票数：看到多数在哪边，后来的人会跟着多数走。
 */
export function PollCard({ post, onRequireLogin }: { post: Post; onRequireLogin?: () => void }) {
  const { t, tp } = useI18n()
  const { compactCount } = useFormat()
  const vote = useVote()
  const poll = post.poll
  if (!poll) return null

  const showResults = poll.total_votes !== null
  const total = poll.total_votes ?? 0
  const maxVotes = Math.max(0, ...poll.options.map((o) => o.votes ?? 0))

  const choose = (optionId: string) => {
    if (!post.viewer) {
      onRequireLogin?.()
      return
    }
    vote.mutate({ postId: post.id, optionId })
  }

  return (
    // 卡片里的点击不能冒到帖子上跳进详情
    <div className="mt-3 space-y-2" onClick={(event) => event.stopPropagation()}>
      {poll.options.map((option) => {
        if (!showResults) {
          return (
            <button
              key={option.id}
              type="button"
              disabled={vote.isPending || poll.closed}
              onClick={() => choose(option.id)}
              className="w-full rounded-md border border-brand-ink px-3 py-2 text-left text-[15px] font-bold text-brand-ink transition-colors hover:bg-brand-ink/10 disabled:opacity-60"
            >
              {option.label}
            </button>
          )
        }
        const votes = option.votes ?? 0
        const percent = total > 0 ? Math.round((votes / total) * 100) : 0
        const mine = option.id === poll.voted_option_id
        const leading = poll.closed && votes > 0 && votes === maxVotes
        return (
          <div
            key={option.id}
            className="relative overflow-hidden rounded-md"
            aria-label={t('poll.optionResult', { label: option.label, percent })}
          >
            <div
              aria-hidden
              className={cn('absolute inset-y-0 left-0', leading ? 'bg-brand/70' : 'bg-brand/30')}
              style={{ width: `${percent}%` }}
            />
            <div className="relative flex items-center gap-2 px-3 py-2 text-[15px]">
              <span className={cn('min-w-0 flex-1 truncate', leading && 'font-bold')}>
                {option.label}
              </span>
              {mine && (
                <Check className="size-4 shrink-0 text-brand-ink" aria-label={t('poll.yourVote')} />
              )}
              <span className={cn('shrink-0 font-mono text-[14px]', leading && 'font-bold')}>
                {percent}%
              </span>
            </div>
          </div>
        )
      })}
      <p className="text-[13px] text-ink-muted">
        {showResults ? tp('poll.votes', total, { count: compactCount(total) }) : t('poll.hidden')}
        {' · '}
        {poll.closed ? t('poll.closed') : <TimeLeft endsAt={poll.ends_at} />}
      </p>
    </div>
  )
}

function TimeLeft({ endsAt }: { endsAt: string }) {
  const { tp } = useI18n()
  const minutes = Math.max(1, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 60_000))
  if (minutes < 60) return <>{tp('poll.minutesLeft', minutes)}</>
  const hours = Math.round(minutes / 60)
  if (hours < 24) return <>{tp('poll.hoursLeft', hours)}</>
  return <>{tp('poll.daysLeft', Math.round(hours / 24))}</>
}
